// FT-PRICING-UI: public pricing page. Free vs Pro cards with a feature
// matrix. Logged-in users hit /api/billing/checkout; anonymous users
// are sent to /signup?next=/pricing.

import { useState } from "preact/hooks";
import { useAuth, useNavigate } from "../contexts/AppContext";
import { PublicLayout } from "./PublicLayout";

const features = [
  { label: "Portals", free: "1", pro: "Unlimited" },
  { label: "AI chat turns", free: "Limited", pro: "15× more" },
  { label: "Analytics", free: false, pro: true },
  { label: "Contact form", free: false, pro: true },
  { label: "Custom embeds", free: false, pro: true },
  { label: "Full SEO controls", free: false, pro: true },
];

export function Pricing() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const authed = auth.status === "authed";
  const [busy, setBusy] = useState(false);

  const handleUpgrade = async () => {
    if (!authed) {
      navigate("/signup?next=/pricing");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/billing/checkout", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || "Checkout unavailable.");
        return;
      }
      window.location.href = data.redirect_url;
    } finally {
      setBusy(false);
    }
  };

  const loading = auth.status === "loading";

  return (
    <PublicLayout>
      <div class="container py-5 flex-grow-1" style="max-width: 960px">
        <div class="text-center mb-5">
          <h1 class="h2 mb-2">Simple pricing</h1>
          <p class="text-muted mb-0">Start free. Upgrade when you need more.</p>
        </div>

        <div class="row g-4 justify-content-center">
          <div class="col-md-5">
            <div class="card h-100">
              <div class="card-body d-flex flex-column">
                <h2 class="h5">Free</h2>
                <div class="mb-3">
                  <span class="h3">$0</span>
                  <span class="text-muted"> / forever</span>
                </div>
                <ul class="list-unstyled flex-grow-1 mb-4">
                  {features.map((f, i) => (
                    <li key={i} class="d-flex justify-content-between py-2 border-bottom">
                      <span>{f.label}</span>
                      <span class="text-muted">
                        {typeof f.free === "boolean"
                          ? f.free
                            ? "✓"
                            : "—"
                          : f.free}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  class="btn btn-outline-secondary w-100"
                  onClick={() => navigate("/create")}
                >
                  Get started
                </button>
              </div>
            </div>
          </div>

          <div class="col-md-5">
            <div class="card h-100 border-primary">
              <div class="card-body d-flex flex-column">
                <h2 class="h5">Pro</h2>
                <div class="mb-3">
                  <span class="h3">$12</span>
                  <span class="text-muted"> / month</span>
                </div>
                <ul class="list-unstyled flex-grow-1 mb-4">
                  {features.map((f) => (
                    <li key={f.label} class="d-flex justify-content-between py-2 border-bottom">
                      <span>{f.label}</span>
                      <span class="text-muted">
                        {typeof f.pro === "boolean"
                          ? f.pro
                            ? "✓"
                            : "—"
                          : f.pro}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  class="btn btn-primary w-100"
                  onClick={handleUpgrade}
                  disabled={busy || loading}
                >
                  {busy
                    ? "Redirecting…"
                    : !authed
                      ? "Sign up to upgrade"
                      : "Upgrade to Pro"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
